'use client'

import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Button, Cell, Container, Dialog, Spacer, Text } from '@vinted/web-ui'
import { QuestionCircle24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  clickEvent,
  viewEvent,
  viewScreenEvent,
  BumpOptionModel,
  BumpOptionsModel,
} from '@marketplace-web/vas/vas-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import BumpOptionList from './BumpOptionList'
import { ItemSelection } from '../../ItemSelection'
import { generateBumpMotivationText } from '../../../../utils/bumpMotivationText'

enum ReviewOrderActionTargetDetails {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

type Props = {
  show: boolean
  isOrderLoading: boolean
  isBumpOptionsLoading: boolean
  bumpOptions?: BumpOptionsModel
  selectedOption: BumpOptionModel | null
  onSelectedOptionChange: (selectedBumpOption: BumpOptionModel) => void
  onAddButtonClick: () => void
  onConfirm: () => void
  onCancel: () => void
  onHelpButtonClick: () => void
}

const BumpSelectionConfirmationModal = ({
  show,
  isOrderLoading,
  isBumpOptionsLoading,
  bumpOptions,
  selectedOption,
  onSelectedOptionChange,
  onCancel,
  onConfirm,
  onAddButtonClick,
  onHelpButtonClick,
}: Props) => {
  const { locale } = useIntl()

  const isEmptyBumpOrderFixEnabled = useFeatureSwitch('empty_bump_order_fix_web')

  const { track } = useTracking()
  const translate = useTranslate('bump_items.selection_confirmation_modal')
  const a11yTranslate = useTranslate('bump_items.a11y')
  const addMoreItemstoBumpV2AbTest = useAbTest('add_more_items_to_bump_v2')

  const orderItemsCount = selectedOption?.pushUpOrderItems.length
  const showMultiBumpDiscountOnAddMoreButton =
    addMoreItemstoBumpV2AbTest?.variant === 'b' &&
    !!bumpOptions?.multiBumpDiscount.discountPercentage

  useEffect(() => {
    if (!show) return

    track(
      viewScreenEvent({
        screen: 'push_up_order_review',
      }),
    )
  }, [show, track])

  useEffect(() => {
    if (!isEmptyBumpOrderFixEnabled) return
    if (orderItemsCount === undefined) return

    track(
      viewEvent({
        screen: 'push_up_order_review',
        target: 'bump_precheckout_review_order_action',
        targetDetails: orderItemsCount
          ? ReviewOrderActionTargetDetails.Enabled
          : ReviewOrderActionTargetDetails.Disabled,
      }),
    )
  }, [orderItemsCount, isEmptyBumpOrderFixEnabled, track])

  function handleConfirm(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isOrderLoading || isBumpOptionsLoading) {
      event.preventDefault()

      return
    }

    if (isOrderLoading || !selectedOption) return

    onConfirm()

    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'review_push_up_order',
        targetDetails: selectedOption.days.toString(),
      }),
    )
  }

  function handleCancel() {
    onCancel()

    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'close_screen',
      }),
    )
  }

  function handleAddButtonClick() {
    onAddButtonClick()
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'multiple_push_up',
      }),
    )
  }

  function handleHelpButtonClick() {
    onHelpButtonClick()
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'push_up_learn_more',
      }),
    )
  }

  function handleBumpOptionChange(selectedBumpOption: BumpOptionModel) {
    onSelectedOptionChange(selectedBumpOption)
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'push_up_duration_change',
        targetDetails: JSON.stringify({
          dutation: selectedBumpOption.days.toString(),
          is_international: selectedBumpOption.international,
        }),
      }),
    )
  }

  function getOrderPriceLabel() {
    return selectedOption ? formatCurrencyAmount(selectedOption.payable, locale) : ''
  }

  function renderTitle() {
    return (
      <Cell
        styling={Cell.Styling.Narrow}
        prefix={<Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.X3Large} />}
        suffix={
          <Button
            iconName={QuestionCircle24}
            styling={Button.Styling.Flat}
            onClick={handleHelpButtonClick}
            testId="bump-help-button"
            aria={{ 'aria-label': a11yTranslate('actions.learn_more') }}
          />
        }
      >
        <Text
          as="h2"
          id="bump_selection_modal_title"
          type={Text.Type.Title}
          alignment={Text.Alignment.Center}
          text={translate('title')}
          width={Text.Width.Parent}
        />
      </Cell>
    )
  }

  function renderMotivation() {
    if (!bumpOptions) return null
    if (!orderItemsCount) return null

    const hideMultiBumpDiscountInMotivation = showMultiBumpDiscountOnAddMoreButton

    const motivationText = generateBumpMotivationText({
      bumpOptions,
      orderItemsCount,
      hideMultiBumpDiscount: hideMultiBumpDiscountInMotivation,
      translate,
    })

    if (!motivationText) return null

    return (
      <Cell styling={Cell.Styling.Narrow}>
        <Text
          as="span"
          format
          type={Text.Type.Subtitle}
          width={Text.Width.Parent}
          alignment={Text.Alignment.Center}
          text={motivationText}
          theme="success"
          testId="bump-motivation-label"
        />
      </Cell>
    )
  }

  function renderBumpOptions() {
    if (!bumpOptions?.options.length || !selectedOption) return null

    return (
      <BumpOptionList
        bumpOptions={bumpOptions.options}
        selectedOptionId={selectedOption.id}
        onSelect={handleBumpOptionChange}
        isLoading={isBumpOptionsLoading}
      />
    )
  }

  function renderSalesTaxNote() {
    if (!selectedOption?.salesTax) return null

    return (
      <Text
        as="span"
        alignment={Text.Alignment.Center}
        text={translate('duration.details.sales_tax_note')}
        testId="bump-selection-confirmation-modal--sales-tax-note"
        width={Text.Width.Parent}
      />
    )
  }

  function renderBody() {
    if (!bumpOptions) return null

    const { bumpableItemCount, freePushUpsCount, discountPercentage, multiBumpDiscount } =
      bumpOptions

    const isEligibleForMultiBumpDiscount =
      discountPercentage === 0 &&
      bumpableItemCount - freePushUpsCount >= multiBumpDiscount.minPaidItemCount

    return (
      <Container>
        <ItemSelection
          orderItems={selectedOption?.pushUpOrderItems}
          onAddButtonClick={handleAddButtonClick}
          multiBumpDiscount={
            showMultiBumpDiscountOnAddMoreButton && isEligibleForMultiBumpDiscount
              ? bumpOptions.multiBumpDiscount.discountPercentage
              : undefined
          }
        />
        <Spacer size={Spacer.Size.Large} />
        <SeparatedList separator={<Spacer orientation={Spacer.Orientation.Horizontal} />}>
          {renderBumpOptions()}
          {renderSalesTaxNote()}
        </SeparatedList>
      </Container>
    )
  }

  function renderActions() {
    const amount = getOrderPriceLabel()
    const isEmptyOrder = isEmptyBumpOrderFixEnabled && !orderItemsCount

    return (
      <Cell>
        <Button
          isLoading={isOrderLoading || isBumpOptionsLoading}
          disabled={!selectedOption || isBumpOptionsLoading || isEmptyOrder}
          styling={Button.Styling.Filled}
          text={translate('actions.review_order', { amount })}
          onClick={handleConfirm}
          testId="bump-review-order-button"
        />
        <Spacer />
        <Button
          styling={Button.Styling.Flat}
          text={translate('actions.cancel')}
          aria={{ 'aria-label': a11yTranslate('actions.close') }}
          onClick={handleCancel}
        />
      </Cell>
    )
  }

  return (
    <Dialog
      aria={{ labelledby: 'bump_selection_modal_title' }}
      show={show}
      hasScrollableContent
      testId="bump-selection-confirmation-modal"
    >
      <div className="u-fill-width">
        {renderTitle()}
        <ScrollableArea>
          {renderMotivation()}
          {renderBody()}
        </ScrollableArea>
        {renderActions()}
      </div>
    </Dialog>
  )
}

export default BumpSelectionConfirmationModal
